/**
 * 問い合わせ管理の Query Hooks
 * TanStack Query を使用したデータフェッチングとキャッシュ管理
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthHeader } from '@/hooks/useAuth';
import {
  createAdminMessageAuthenticated,
  getInquiryAttachmentUrlAuthenticated,
  getInquiryAuthenticated,
  listInquiriesAuthenticated,
  updateInquiryCategoryAuthenticated,
  updateInquiryStatusAuthenticated,
} from '@/lib/api/mise/inquiries';
import { inquiryKeys } from '../keys';
import type {
  CreateAdminMessageRequest,
  InquiryCategory,
  InquiryStatus,
  ListInquiriesParams,
} from '@/lib/types/mise/inquiry';

/** 一覧取得のキャッシュ時間（最終更新時刻が頻繁に変動するため短め） */
const INQUIRIES_LIST_STALE_TIME = 30 * 1000; // 30 秒
/** 詳細取得のキャッシュ時間 */
const INQUIRY_DETAIL_STALE_TIME = 30 * 1000;

/**
 * 問い合わせ一覧を取得する Query Hook
 */
export function useInquiries(params: ListInquiriesParams) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: inquiryKeys.list(params as Record<string, unknown>),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return listInquiriesAuthenticated(authHeaders, params);
    },
    enabled: !authHeaderLoading && isAuthorized,
    staleTime: INQUIRIES_LIST_STALE_TIME,
  });
}

/**
 * 問い合わせ詳細を取得する Query Hook
 */
export function useInquiry(id: string | undefined) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: inquiryKeys.detail(id ?? ''),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getInquiryAuthenticated(id!, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!id,
    staleTime: INQUIRY_DETAIL_STALE_TIME,
  });
}

/**
 * 管理者返信を追加する Mutation Hook
 * 成功時は対象 inquiry の詳細・一覧キャッシュを無効化する。
 */
export function useCreateAdminMessage(inquiryId: string) {
  const queryClient = useQueryClient();
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async (request: CreateAdminMessageRequest) => {
      const authHeaders = getAuthHeader();
      return createAdminMessageAuthenticated(inquiryId, request, authHeaders);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inquiryKeys.detail(inquiryId) });
      queryClient.invalidateQueries({ queryKey: inquiryKeys.all() });
    },
  });
}

/**
 * 問い合わせのステータスを更新する Mutation Hook
 */
export function useUpdateInquiryStatus(inquiryId: string) {
  const queryClient = useQueryClient();
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async (status: InquiryStatus) => {
      const authHeaders = getAuthHeader();
      return updateInquiryStatusAuthenticated(inquiryId, { status }, authHeaders);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inquiryKeys.detail(inquiryId) });
      queryClient.invalidateQueries({ queryKey: inquiryKeys.all() });
    },
  });
}

/**
 * 問い合わせのカテゴリを更新する Mutation Hook
 */
export function useUpdateInquiryCategory(inquiryId: string) {
  const queryClient = useQueryClient();
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async (category: InquiryCategory) => {
      const authHeaders = getAuthHeader();
      return updateInquiryCategoryAuthenticated(inquiryId, { category }, authHeaders);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inquiryKeys.detail(inquiryId) });
      queryClient.invalidateQueries({ queryKey: inquiryKeys.all() });
    },
  });
}

/**
 * 添付ファイルの presigned GET URL を取得する関数を返す。
 * URL は短期 (15 分) 有効なため、毎クリックで新規取得する。React Query は使わない。
 */
export function useFetchAttachmentUrl() {
  const { getAuthHeader } = useAuthHeader();

  return async (inquiryId: string, attachmentId: string): Promise<string> => {
    const authHeaders = getAuthHeader();
    const response = await getInquiryAttachmentUrlAuthenticated(
      inquiryId,
      attachmentId,
      authHeaders,
    );
    return response.url;
  };
}
